import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getOrganizationSchema, getBreadcrumbSchema } from '@/components/feature/SchemaOrg';

const milestones = [
  { year: '2018', title: '平台成立', desc: '男性健康科普中心正式成立，专注男性生殖健康领域的知识传播。' },
  { year: '2019', title: '内容体系建设', desc: '完成龟头责五大分类（症状、原因、治疗、预防、用药）的内容框架搭建。' },
  { year: '2020', title: '专家团队扩展', desc: '邀请多位三甲医院泌尿外科和男科专家加盟内容审核团队。' },
  { year: '2022', title: '百万读者', desc: '全年累计服务读者超100万人次，科普内容被多家主流平台转载。' },
  { year: '2024', title: '内容全面升级', desc: '完成全部科普文章的循证医学审查，确保内容权威性和时效性。' },
  { year: '2026', title: '持续深耕', desc: '持续为男性提供最专业、易懂的生殖健康科普服务。' },
];

const teamMembers = [
  { name: '王建国', title: '医学顾问', role: '三甲医院泌尿外科主任医师，30年临床经验', icon: 'ri-user-star-line' },
  { name: '李明远', title: '内容主编', role: '医学博士，男科研究方向，10年科普写作经验', icon: 'ri-quill-pen-line' },
  { name: '张思远', title: '审核专家', role: '副主任医师，泌尿外科专业，负责内容医学审核', icon: 'ri-shield-check-line' },
  { name: '陈晓华', title: '编辑主管', role: '医学编辑，负责科普内容的策划与编辑工作', icon: 'ri-edit-line' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqbfsnlsngrm4lm400', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError('提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 男性健康科普中心"
        description="男性健康科普中心专注于男性生殖健康科普，提供龟头责等男性常见疾病的权威知识。了解我们的品牌历史、专业团队与联系方式。"
        keywords="男性健康科普,龟头责科普,关于我们,男性生殖健康"
        canonicalUrl="https://www.nanke-health.com/about"
        ogType="website"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([
        { name: '首页', url: 'https://www.nanke-health.com' },
        { name: '关于我们', url: 'https://www.nanke-health.com/about' },
      ])) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <div className="pt-24 pb-10 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ name: '首页', href: '/' }, { name: '关于我们' }]} />
            <div className="mt-6">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                关于男性健康科普中心
              </h1>
              <p className="text-foreground-600 max-w-2xl leading-relaxed">
                我们是一家专注于男性生殖健康科普的权威平台，致力于通过专业、易懂、实用的医学科普内容，帮助广大男性朋友正确认识和管理自身健康。
              </p>
            </div>
          </div>
        </div>

        {/* Brand Story */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    男性健康科普中心成立于2018年，源于创始团队对男性生殖健康知识普及不足的深刻认识。我们发现，许多男性由于缺乏正确的医学知识，在面临龟头责等问题时，往往会因为羞于启齿而拖延就医，最终导致病情加重。
                  </p>
                  <p>
                    我们的使命是打破沉默，用专业、温暖、易懂的科普内容，让每位男性都能掌握基本的生殖健康知识。我们坚信，<strong>知识是最好的预防手段</strong>，了解自己的身体，是守护健康的第一步。
                  </p>
                  <p>
                    截至目前，我们的科普内容已累计服务超过<strong className="text-accent-600">500万人次</strong>的读者，获得了广大男性朋友和医疗专业人士的认可和信赖。
                  </p>
                </div>
              </div>
              <div className="flex-1 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20medical%20office%20interior%20with%20soft%20cream%20and%20beige%20tones%2C%20professional%20yet%20welcoming%20healthcare%20environment%2C%20clean%20aesthetic%20with%20natural%20lighting%2C%20editorial%20photography%20style%20with%20gentle%20shadows%20and%20organic%20textures&width=800&height=500&seq=about-brand-01&orientation=landscape"
                  alt="品牌形象"
                  className="w-full h-72 md:h-80 object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {milestones.map((m, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <span className="inline-block px-2.5 py-1 text-xs font-bold bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                    {m.year}
                  </span>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{m.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              专业团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-full mb-4">
                    <i className={`${member.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{member.name}</h3>
                  <p className="text-xs font-medium text-primary-600 mb-2">{member.title}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              联系我们
            </h2>
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">contact@nanke-health.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1">咨询热线</h4>
                    <p className="text-sm text-foreground-600">400-123-4567（工作日 9:00-18:00）</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1">办公地址</h4>
                    <p className="text-sm text-foreground-600">北京市朝阳区建国路88号健康大厦15层</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 mt-4">
                  <strong className="text-foreground-600">重要提示：</strong>本站为科普平台，不提供在线诊疗服务。如有健康问题，请前往正规医疗机构就诊。
                </p>
              </div>

              <div className="flex-1">
                <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4" data-readdy-form="">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">给我们留言</h3>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="website-url-field"
                  />

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 bg-secondary-100 text-secondary-800 rounded-md text-sm">
                      <i className="ri-check-line mr-1.5"></i>
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-accent-100 text-accent-800 rounded-md text-sm">
                      <i className="ri-error-warning-line mr-1.5"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-4 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : (
                      '发送留言'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}